import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { SelectOption } from 'src/app/components/form/select-field/select-field.config';
import { TextAreaFieldComponent } from 'src/app/components/form/textarea-field/textarea-field.component';
import { ProjectFinanceService } from 'src/app/services/api/project-finance.service';
import { OptionService } from 'src/app/services/api/option.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CommonService } from 'src/app/services/common.service';
import { RouteService } from 'src/app/services/route.service';
import {
  PROJECT_FINANCE_DEFAULT_CURRENCY,
  ProjectFinanceCost,
  ProjectFinanceCostPayload
} from 'src/app/models/entities/project-finance';

const EMPTY_OPTION: SelectOption = { key: '', value: '-- select' };

@Component({
  selector: 'projects-finance-testing-cost-form',
  imports: [ReactiveFormsModule, CurrencyPipe, LoaderComponent, InputFieldComponent, SelectFieldComponent, TextAreaFieldComponent],
  templateUrl: './projects-finance-testing-cost-form.component.html',
  styleUrl: './projects-finance-testing-cost-form.component.scss'
})
export class ProjectsFinanceTestingCostFormComponent implements OnInit, OnDestroy {
  @Input() projectId!: number;
  @Input() projectFinanceCostId: number | null = null;

  resourceOptions: SelectOption[] = [EMPTY_OPTION];
  isLoading = false;
  isSaving = false;
  rowTotal = 0;

  readonly currency = PROJECT_FINANCE_DEFAULT_CURRENCY;

  form = new FormGroup({
    resourceUserId: new FormControl<number | string | null>(null, Validators.required),
    hoursWorked: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    hourlyRate: new FormControl<number | null>(null, [Validators.required, Validators.min(0)]),
    activityDescription: new FormControl<string | null>(null),
    date: new FormControl<string | null>(null, Validators.required)
  });

  private destroy$ = new Subject<void>();

  constructor(
    private projectFinanceService: ProjectFinanceService,
    private optionService: OptionService,
    private alertNotificationService: AlertNotificationService,
    private commonService: CommonService,
    private routeService: RouteService
  ) { }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateRowTotal());

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isEditMode(): boolean {
    return this.projectFinanceCostId !== null;
  }

  getControl(name: keyof typeof this.form.controls): FormControl {
    return this.form.controls[name] as FormControl;
  }

  cancel(): void {
    this.routeService.projectsFinanceAnalysis(this.projectId);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertNotificationService.warning('Please fill in all required fields');
      return;
    }

    const value = this.form.getRawValue();
    const payload: ProjectFinanceCostPayload = {
      resourceUserId: Number(value.resourceUserId),
      hoursWorked: Number(value.hoursWorked),
      hourlyRate: Number(value.hourlyRate),
      activityDescription: value.activityDescription?.trim() || null,
      date: value.date as string
    };

    const request$ = this.isEditMode
      ? this.projectFinanceService.updateTestingCost(this.projectId, this.projectFinanceCostId as number, payload)
      : this.projectFinanceService.createTestingCost(this.projectId, payload);

    this.isSaving = true;
    request$.subscribe({
      next: () => {
        this.isSaving = false;
        this.alertNotificationService.success(this.isEditMode ? 'Testing cost updated successfully' : 'Testing cost added successfully');
        this.routeService.projectsFinanceAnalysis(this.projectId);
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error saving testing cost:', error);
        this.alertNotificationService.error(this.projectFinanceService.errorMessage(error, 'Error saving testing cost'));
      }
    });
  }

  private updateRowTotal(): void {
    const hours = Number(this.form.controls.hoursWorked.value) || 0;
    const rate = Number(this.form.controls.hourlyRate.value) || 0;
    this.rowTotal = Math.round(hours * rate * 100) / 100;
  }

  private loadData(): void {
    this.isLoading = true;
    forkJoin({
      users: this.optionService.projectFinanceUsers(),
      costs: this.isEditMode ? this.projectFinanceService.listTestingCosts(this.projectId) : of(null) as Observable<any>
    }).subscribe({
      next: ({ users, costs }) => {
        this.isLoading = false;
        const userOptions: SelectOption[] = users?.data?.options ?? [];

        const cost = this.projectFinanceService.readResponseKey<ProjectFinanceCost[]>(costs, 'projectFinanceCosts')
          ?.find(item => item.projectFinanceCostId === this.projectFinanceCostId) ?? null;

        const resourceUser = cost?.resourceUser;
        if (resourceUser && !userOptions.some(option => String(option.key) === String(resourceUser.userId))) {
          userOptions.push({ key: resourceUser.userId, value: `${resourceUser.firstName} ${resourceUser.lastName}`.trim() });
        }
        this.resourceOptions = [EMPTY_OPTION, ...userOptions];

        if (this.isEditMode && !cost) {
          this.alertNotificationService.error('Testing cost not found');
          return;
        }
        this.form.patchValue(cost
          ? {
            resourceUserId: resourceUser?.userId ?? '',
            hoursWorked: cost.hoursWorked,
            hourlyRate: cost.hourlyRate,
            activityDescription: cost.activityDescription,
            date: cost.date
          }
          : { resourceUserId: '', date: this.commonService.formatDateOnly(new Date()) });
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading the users list:', error);
        this.alertNotificationService.error(this.projectFinanceService.errorMessage(error, 'Failed to load the users list.'));
      }
    });
  }
}
